<script setup lang="ts">
import type { DialogContentEmits, DialogContentProps } from "reka-ui"
import type { HTMLAttributes } from "vue"
import { reactiveOmit } from "@vueuse/core"
import { X } from "lucide-vue-next"
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  useForwardPropsEmits,
} from "reka-ui"
import { cn } from "@/lib/utils"

const props = defineProps<DialogContentProps & { class?: HTMLAttributes["class"] }>()
const emits = defineEmits<DialogContentEmits>()

const delegatedProps = reactiveOmit(props, "class")

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <DialogPortal>
    <DialogOverlay
      class="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:duration-150 data-[state=closed]:duration-100"
    />
    <!-- ⛔ NEVER PUT A `slide-*` CLASS ON THIS ELEMENT. Stock shadcn ships four of
         them here — the half-translate pair `slide-in-from-left-<half>` and
         `slide-in-from-top-<48pct>`, plus their exits (spelled with placeholders
         because Tailwind scans this file as plain text and would compile a real
         class name straight out of this comment) — and they are a v3-only
         recipe: Tailwind v4 compiles the centring
         `-translate-x-1/2 -translate-y-1/2` to the `translate` PROPERTY, while the
         animate plugin's keyframes write `transform`. Two different properties, so
         they ADD instead of replacing — the slide contributes a second −50 % and
         the modal flies in diagonally from −100 %, −98 %. Fade and zoom are safe
         (opacity, and a scale that composes harmlessly with the translate).
         This is the only element in the app where a slide would cross a centring
         translate; the sheet slides from an edge and is fine, and the menus sit
         inside reka's positioned wrapper so their own transform is free.
         ⚠ Duration and easing are state-scoped on purpose: what ARRIVES takes
         150 ms and decelerates, what LEAVES takes 100 ms — an exit slower than its
         entrance reads as the app dragging its feet. -->
    <DialogContent
      v-bind="forwarded"
      :class="
        cn(
          'fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto -translate-x-1/2 -translate-y-1/2 gap-4 border bg-background p-6 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=open]:duration-150 data-[state=closed]:duration-100 data-[state=open]:ease-out data-[state=closed]:ease-in sm:rounded-lg',
          props.class,
        )"
      @pointer-down-outside="(e) => e.preventDefault()"
    >
      <slot />

      <DialogClose
        class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
      >
        <X class="w-4 h-4" />
        <span class="sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
